import { useEffect, useRef } from 'react';
import { fetchAIOpening } from '../../../utils/api';

/**
 * Openingメッセージ管理フック（opening 取得の単一入口）
 * hydrating = 履歴ロード中の準備中、openingFetching = opening 取得中の準備中
 *
 * @param {string} projectId - プロジェクトID
 * @param {string} userLoginId - ユーザーログインID
 * @param {boolean} historyLoading - 履歴読み込み状態
 * @param {array} messages - メッセージ配列
 * @param {function} setMessages - メッセージ設定関数
 * @param {object} aiConfig - AI設定
 * @param {object} aiConfigError - AI設定エラー
 * @param {object} previousModelTypeRef - 前回のモデルタイプ参照
 * @param {function} setOpeningFetching - Opening取得中状態設定関数
 * @returns {void}
 */
export function useAIOpeningMessage(
    projectId,
    userLoginId,
    historyLoading,
    messages,
    setMessages,
    aiConfig,
    aiConfigError,
    previousModelTypeRef,
    setOpeningFetching
) {
    const aiParametersFetchingRef = useRef(false);

    useEffect(() => {
        if (!userLoginId) {
            setOpeningFetching(false);
            return;
        }
        if (historyLoading) {
            // 履歴完了後に再実行される。メッセージ空なら準備中表示を維持
            return;
        }
        if (!aiConfig) {
            if (aiConfigError) {
                setOpeningFetching(false);
            }
            return;
        }

        const modelTypeChanged = previousModelTypeRef.current !== null &&
            aiConfig?.model_type != null &&
            previousModelTypeRef.current !== aiConfig?.model_type;
        const isOnlyOpeningMessage = messages.length === 1 &&
            messages[0]?.role === 'assistant' &&
            messages[0]?.id?.startsWith('opening-');

        if (messages.length > 0 && !(modelTypeChanged && isOnlyOpeningMessage)) {
            if (!modelTypeChanged && aiConfig?.model_type) {
                previousModelTypeRef.current = aiConfig.model_type;
            }
            setOpeningFetching(false);
            return;
        }

        if (!projectId) {
            setOpeningFetching(false);
            return;
        }
        if (aiConfigError) {
            setOpeningFetching(false);
            return;
        }

        // 既に fetch 中なら触らない（openingFetching は開始時に true 済み）
        if (aiParametersFetchingRef.current) {
            return;
        }

        const modelId = aiConfig?.function_model_id || 'disaster_tankyu';
        const age = aiConfig?.default_age || '高学年';
        const openingParams = { age };
        if (modelId === 'disaster_tankyu') {
            if (aiConfig?.default_tsunami) openingParams.tsunami = aiConfig.default_tsunami;
            if (aiConfig?.default_disaster_type) openingParams.disaster_type = aiConfig.default_disaster_type;
        } else if (modelId === 'disaster_bigbrother') {
            if (aiConfig?.default_disaster_type) openingParams.disaster_type = aiConfig.default_disaster_type;
        }
        let aborted = false;
        aiParametersFetchingRef.current = true;
        setOpeningFetching(true);
        fetchAIOpening(projectId, modelId, openingParams)
            .then((res) => {
                if (aborted) return;
                const opening = res?.opening?.trim();
                if (opening) {
                    setMessages([{
                        role: 'assistant',
                        content: opening,
                        id: `opening-${Date.now()}`,
                        created_at: new Date().toISOString()
                    }]);
                    if (aiConfig?.model_type) {
                        previousModelTypeRef.current = aiConfig.model_type;
                    }
                } else if (aiConfig?.model_type) {
                    previousModelTypeRef.current = aiConfig.model_type;
                }
            })
            .catch((err) => {
                const errorData = err?.response?.data;
                const errorCode = errorData?.error;
                if (errorCode === 'ai_config_not_found' || errorCode === 'ai_disabled' || errorCode === 'api_key_not_configured') {
                    // 既にチェック済みなので無視
                }
            })
            .finally(() => {
                aiParametersFetchingRef.current = false;
                setOpeningFetching(false);
            });
        return () => {
            aborted = true;
            aiParametersFetchingRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        userLoginId,
        historyLoading,
        messages.length,
        projectId,
        aiConfigError,
        aiConfig?.model_type,
        aiConfig?.function_model_id,
        aiConfig?.default_age,
        aiConfig?.default_tsunami,
        aiConfig?.default_disaster_type
    ]);
}
